/**
 * Token Encryption Utility
 * Uses Web Crypto API to encrypt/decrypt tokens stored in browser storage
 *
 * Note: Client-side encryption provides obfuscation but not complete security.
 * The main protection is against casual inspection of storage.
 */

// Generate a consistent key from browser/app-specific data
async function getEncryptionKey() {
  // Combine multiple factors to create a unique but consistent key source
  const keyMaterial = [
    navigator.userAgent,
    window.location.origin,
    'school-portal-secret-v1' // App-specific salt
  ].join('|');

  // Convert to bytes
  const encoder = new TextEncoder();
  const data = encoder.encode(keyMaterial);

  // Create a hash to use as key material
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  // Import as a CryptoKey for AES-GCM
  return crypto.subtle.importKey(
    'raw',
    hashBuffer,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt a string value
 * @param {string} plainText - The value to encrypt
 * @returns {Promise<string>} - Base64 encoded encrypted value with IV
 */
export async function encryptToken(plainText) {
  if (!plainText) return null;

  try {
    const key = await getEncryptionKey();
    const encoder = new TextEncoder();
    const data = encoder.encode(plainText);

    // Generate a random IV for each encryption
    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encryptedData = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      data
    );

    // Combine IV + encrypted data and encode as base64
    const combined = new Uint8Array(iv.length + encryptedData.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encryptedData), iv.length);

    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('Encryption failed:', error);
    // Fallback to plain storage if encryption fails
    return plainText;
  }
}

/**
 * Decrypt an encrypted string value
 * @param {string} encryptedText - Base64 encoded encrypted value
 * @returns {Promise<string>} - Decrypted plain text
 */
export async function decryptToken(encryptedText) {
  if (!encryptedText) return null;

  try {
    const key = await getEncryptionKey();

    // Decode base64
    const combined = new Uint8Array(
      atob(encryptedText).split('').map(c => c.charCodeAt(0))
    );

    // Extract IV and encrypted data
    const iv = combined.slice(0, 12);
    const encryptedData = combined.slice(12);

    const decryptedData = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      encryptedData
    );

    const decoder = new TextDecoder();
    return decoder.decode(decryptedData);
  } catch (error) {
    console.error('Decryption failed:', error);
    // If decryption fails, the value might be unencrypted (legacy)
    // Return as-is to allow gradual migration
    return encryptedText;
  }
}

/**
 * Check if a value appears to be encrypted (base64 with expected length)
 * @param {string} value - The value to check
 * @returns {boolean}
 */
export function isEncrypted(value) {
  if (!value) return false;
  try {
    // Encrypted tokens will be base64 and longer than original JWT
    // JWTs are also base64-ish but have dots separating parts
    const hasJwtStructure = value.split('.').length === 3;
    return !hasJwtStructure && value.length > 50;
  } catch {
    return false;
  }
}
